import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { X } from "lucide-react";

export default function FirstTimeAuthModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user is already logged in or has dismissed popup in this session, do not run timer
    const hasDismissed = sessionStorage.getItem("hasDismissedFirstTimeAuthModal");
    if (isLoggedIn || hasDismissed) {
      return;
    }

    // Set 45-second (45000ms) timer for first-time visitors
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 45000);

    return () => clearTimeout(timer);
  }, [isLoggedIn]);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem("hasDismissedFirstTimeAuthModal", "true");
  };

  const handleContinue = (e) => {
    e.preventDefault();
    handleClose();
    if (inputValue.trim()) {
      navigate("/login", { state: { initialValue: inputValue.trim() } });
    } else {
      navigate("/login");
    }
  };

  const handleGoogleAuth = () => {
    handleClose();
    navigate("/login");
  };

  if (!isOpen || isLoggedIn) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-50 bg-white shadow-md hover:bg-slate-100 rounded-full p-2 text-slate-500 hover:text-slate-800 transition-all border border-slate-100"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        {/* LEFT BANNER SECTION */}
        <div className="md:w-1/2 p-6 sm:p-10 bg-gradient-to-br from-[#EFF6FF] via-[#F4F4FE] to-[#EEF2FF] flex flex-col justify-between relative overflow-hidden">
          {/* Subtle Decorative Wave Graphics */}
          <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 w-44 h-44 rounded-full bg-gradient-to-tr from-blue-400/20 to-purple-400/20 blur-2xl pointer-events-none" />
          <svg
            className="absolute top-0 right-0 w-full h-full opacity-30 pointer-events-none"
            viewBox="0 0 400 400"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 100C150 150 250 50 400 120V0H0V100Z"
              fill="url(#paint0_linear)"
            />
            <defs>
              <linearGradient
                id="paint0_linear"
                x1="0"
                y1="0"
                x2="400"
                y2="120"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#3B82F6" stopOpacity="0.1" />
                <stop offset="1" stopColor="#6366F1" stopOpacity="0.2" />
              </linearGradient>
            </defs>
          </svg>

          <div className="relative z-10 my-auto">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight mb-3">
              Get <br />
              flat <span className="text-[#2563EB]">7% Off</span> on your first order
            </h2>
            <p className="text-base font-bold text-slate-800 mb-6">
              Extra 5% Cashback
            </p>
          </div>

          <div className="relative z-10 pt-4 border-t border-blue-100/60">
            <p className="text-xs font-semibold text-[#2563EB]">
              PS: We send special discounts to our registered users
            </p>
          </div>
        </div>

        {/* RIGHT FORM SECTION */}
        <div className="md:w-1/2 p-6 sm:p-10 flex flex-col justify-center bg-white">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">
            Log In or Sign Up
          </h3>

          <form onSubmit={handleContinue} className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-slate-700 mb-2">
                Mobile or Email ID
              </label>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="eg. 9847372621 or johndoe@abc.com"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium outline-none transition-all placeholder:text-slate-400"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold py-3.5 px-4 rounded-xl shadow-md transition-all active:scale-[0.99] text-sm flex items-center justify-center gap-2"
            >
              Continue
            </button>
          </form>

          <div className="relative my-6 flex items-center justify-center">
            <div className="border-t border-slate-200 w-full" />
            <span className="bg-white px-3 text-xs font-semibold text-slate-400 absolute">
              OR
            </span>
          </div>

          {/* Google Auth Button */}
          <button
            type="button"
            onClick={handleGoogleAuth}
            className="w-full bg-white hover:bg-slate-50 text-slate-700 font-bold py-3 px-4 rounded-xl border border-slate-300 shadow-sm transition-all text-sm flex items-center justify-center gap-3 active:scale-[0.99]"
          >
            <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>

          <p className="mt-6 text-center text-[11px] text-slate-500 leading-normal">
            By continuing, you agree to Mexamedico's{" "}
            <span className="underline cursor-pointer hover:text-slate-800 font-medium">
              Terms of Use
            </span>{" "}
            and{" "}
            <span className="underline cursor-pointer hover:text-slate-800 font-medium">
              Privacy Policy
            </span>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
